/**
 * React Query hooks for map saved sets API
 */
import {
  useQuery,
  useMutation,
  useQueryClient
} from "@tanstack/react-query";
import {
  fetchSavedSetsList,
  fetchSavedSetDetails,
  createSavedSet,
  updateSavedSet,
  deleteSavedSet,
  togglePostalCode,
  togglePriorityElectoralDistrict
} from "./api";
import type {
  CreateSavedSetRequest,
  UpdateSavedSetRequest
} from "./types";

export const SAVED_SET_LIST_QUERY_KEY = ["map-saved-sets", "list"] as const;
export const savedSetDetailsQueryKey = (id: string) =>
  ["map-saved-sets", "details", id] as const;

export interface SavedSetsListOptions {
  enabled?: boolean;
}

export function useSavedSetsList(options?: SavedSetsListOptions) {
  return useQuery({
    queryKey: SAVED_SET_LIST_QUERY_KEY,
    queryFn: fetchSavedSetsList,
    enabled: options?.enabled !== false
  });
}

export interface SavedSetDetailsOptions {
  enabled?: boolean;
}

export function useSavedSetDetails(
  id: string | undefined,
  options?: SavedSetDetailsOptions
) {
  return useQuery({
    queryKey: savedSetDetailsQueryKey(id ?? ""),
    queryFn: () => fetchSavedSetDetails(id!),
    enabled: (options?.enabled !== false && !!id)
  });
}

export function useCreateSavedSet() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: CreateSavedSetRequest) => createSavedSet(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SAVED_SET_LIST_QUERY_KEY });
    }
  });
}

export function useUpdateSavedSet() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...request
    }: { id: string } & UpdateSavedSetRequest) =>
      updateSavedSet(id, request),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: SAVED_SET_LIST_QUERY_KEY });
      queryClient.invalidateQueries({
        queryKey: savedSetDetailsQueryKey(variables.id)
      });
    }
  });
}

export function useDeleteSavedSet() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteSavedSet(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SAVED_SET_LIST_QUERY_KEY });
    }
  });
}

export function useTogglePostalCode() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      savedSetId,
      postalCodeId
    }: {
      savedSetId: string;
      postalCodeId: string;
    }) => togglePostalCode(savedSetId, postalCodeId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: savedSetDetailsQueryKey(variables.savedSetId)
      });
    }
  });
}

export function useTogglePriorityElectoralDistrict() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      savedSetId,
      districtId
    }: {
      savedSetId: string;
      districtId: string;
    }) => togglePriorityElectoralDistrict(savedSetId, districtId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: savedSetDetailsQueryKey(variables.savedSetId)
      });
    }
  });
}
